/**
 * ContentSage Pricing Page
 *
 * Displays subscription tiers:
 * - FREE: 1 credit/month
 * - STARTER: $29/mo ($23.20/mo yearly) - 10 credits
 * - PRO: $79/mo ($63.20/mo yearly) - 50 credits
 * - AGENCY: $199/mo ($159.20/mo yearly) - 150 credits
 *
 * Features:
 * - Monthly/Yearly toggle with 20% discount badge
 * - PRO tier highlighted as "Most Popular"
 * - Feature comparison
 * - Credit costs for tools
 */

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Check,
  Sparkles,
  Zap,
  Crown,
  Building2,
  ArrowLeft,
  Loader2,
} from 'lucide-react';
import ContentSageLogo from '../components/ContentSageLogo';
import { useAuth } from '../contexts/AuthContext';
import {
  usePaymentsEnabled,
  wasBlockedByKillSwitch,
  PAYMENTS_UNAVAILABLE_MESSAGE,
} from '../hooks/usePaymentsEnabled';

const API_BASE = import.meta.env.VITE_API_URL || 'https://claude-essay-agent-production.up.railway.app';

interface PricingTier {
  id: string;
  name: string;
  monthlyPrice: number;
  yearlyPrice: number;
  credits: number | string;
  description: string;
  features: string[];
  popular?: boolean;
  icon: React.ElementType;
  gradient: string;
  ctaText: string;
}

const PRICING_TIERS: PricingTier[] = [
  {
    id: 'free',
    name: 'FREE',
    monthlyPrice: 0,
    yearlyPrice: 0,
    credits: '1',
    description: 'Perfect for trying out ContentSage',
    features: [
      '1 credit/month',
      'AI Infographics',
      'Smart Internal Linking',
      'Basic SEO optimization',
      'Email support',
    ],
    icon: Sparkles,
    gradient: 'from-slate-500/20 to-gray-500/20',
    ctaText: 'Start Free',
  },
  {
    id: 'starter',
    name: 'STARTER',
    monthlyPrice: 29,
    yearlyPrice: 23.2,
    credits: '10',
    description: 'Great for individual creators and bloggers',
    features: [
      '10 credits/month',
      'SEO optimization',
      'AI-generated images (2-3 per article)',
      'Basic analytics',
      'People Also Ask research',
      'Email support',
    ],
    icon: Zap,
    gradient: 'from-purple-500/20 to-indigo-500/20',
    ctaText: 'Get Started',
  },
  {
    id: 'pro',
    name: 'PRO',
    monthlyPrice: 79,
    yearlyPrice: 63.2,
    credits: '50',
    description: 'Ideal for professional content marketers',
    features: [
      '50 credits/month',
      'Knowledge Vault integration',
      'Citation Verification',
      'Bulk generation',
      'Advanced SEO & structured data',
      'Deep keyword research',
      'PDF export',
      'Priority email support',
    ],
    popular: true,
    icon: Crown,
    gradient: 'from-amber-500/20 to-orange-500/20',
    ctaText: 'Go Pro',
  },
  {
    id: 'agency',
    name: 'AGENCY',
    monthlyPrice: 199,
    yearlyPrice: 159.2,
    credits: '150',
    description: 'Perfect for agencies and large teams',
    features: [
      '150 credits/month',
      'Multiple Brand Profiles',
      'White-label reports',
      'Priority support',
      'Dedicated account manager',
      'Custom integrations',
      'API access (coming soon)',
      'Advanced analytics',
    ],
    icon: Building2,
    gradient: 'from-emerald-500/20 to-teal-500/20',
    ctaText: 'Contact Sales',
  },
];

const CREDIT_COSTS = [
  { tool: 'BlogCraft', cost: '1 credit per blog post' },
  { tool: 'PlanCraft', cost: '25 credits per business plan' },
  { tool: 'CopyForge', cost: '5 credits per landing page' },
];

export default function PricingPage() {
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
  const [isLoading, setIsLoading] = useState<string | null>(null);
  const navigate = useNavigate();
  const { user, getAuthToken } = useAuth();
  const { paymentsEnabled, loading: paymentsLoading } = usePaymentsEnabled();
  const [paymentsPaused, setPaymentsPaused] = useState(false);

  const handleGetStarted = async (tierId: string) => {
    // Backend-sourced payments switch; see hooks/usePaymentsEnabled.
    if (!paymentsEnabled || paymentsPaused) return;
    if (tierId === 'free') {
      navigate('/signup');
      return;
    }
    if (tierId === 'agency') {
      window.location.href = 'mailto:sales@contentsage.ai?subject=Agency Plan Inquiry';
      return;
    }

    // If user is logged in, go directly to Stripe Checkout
    if (user) {
      setIsLoading(tierId);
      try {
        const planKey = `${tierId}_${billingCycle}`;
        const token = await getAuthToken();
        const response = await fetch(`${API_BASE}/api/v1/payments/create-checkout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ plan_type: planKey }),
        });

        if (response.ok) {
          const data = await response.json();
          window.location.href = data.checkout_url;
        } else if (await wasBlockedByKillSwitch(response)) {
          // Purchases are paused by the operator switch. Sending them to
          // /signup - which is what this branch used to do for every failure -
          // is nonsense for an already-signed-in user, and hides the reason.
          setPaymentsPaused(true);
        } else {
          // Fallback to signup with selected plan
          navigate('/signup', { state: { selectedPlan: tierId } });
        }
      } catch {
        navigate('/signup', { state: { selectedPlan: tierId } });
      } finally {
        setIsLoading(null);
      }
    } else {
      // Not logged in — go to signup with plan pre-selected
      navigate('/signup', { state: { selectedPlan: tierId } });
    }
  };

  const paymentsNotice =
    !paymentsLoading && (!paymentsEnabled || paymentsPaused) ? (
      <div className="mx-auto mb-8 max-w-2xl rounded-lg border border-amber-400/30 bg-amber-400/10 p-4 text-center">
        <p className="text-sm font-medium text-amber-200">
          {PAYMENTS_UNAVAILABLE_MESSAGE}
        </p>
      </div>
    ) : null;

  return (
    <div className="min-h-screen bg-[#0a0a1a] text-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0a0a1a]/80 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center">
              <ContentSageLogo size={32} showText={true} />
            </Link>
            <Link
              to="/"
              className="flex items-center space-x-2 text-gray-400 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back to Home</span>
            </Link>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="pt-32 pb-20 px-6">
        {paymentsNotice}
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-5xl md:text-6xl font-bold mb-6"
            >
              Choose Your{' '}
              <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-amber-400 bg-clip-text text-transparent">
                Perfect Plan
              </span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-xl text-gray-400 max-w-2xl mx-auto"
            >
              Start free, upgrade anytime. All plans include our core AI writing
              features.
            </motion.p>
          </div>

          {/* Billing Toggle */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="flex items-center justify-center mb-12 space-x-4"
          >
            <span
              className={`text-lg ${
                billingCycle === 'monthly' ? 'text-white font-semibold' : 'text-gray-400'
              }`}
            >
              Monthly
            </span>
            <button
              onClick={() =>
                setBillingCycle(billingCycle === 'monthly' ? 'yearly' : 'monthly')
              }
              className="relative w-16 h-8 bg-gradient-to-r from-purple-500 to-amber-500 rounded-full transition-all"
            >
              <motion.div
                animate={{
                  x: billingCycle === 'monthly' ? 2 : 34,
                }}
                transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                className="absolute top-1 w-6 h-6 bg-white rounded-full shadow-lg"
              />
            </button>
            <span
              className={`text-lg ${
                billingCycle === 'yearly' ? 'text-white font-semibold' : 'text-gray-400'
              }`}
            >
              Yearly
            </span>
            {billingCycle === 'yearly' && (
              <motion.span
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                className="ml-2 px-3 py-1 bg-green-500/20 text-green-400 text-sm font-semibold rounded-full border border-green-500/30"
              >
                Save 20%
              </motion.span>
            )}
          </motion.div>

          {/* Pricing Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-20">
            {PRICING_TIERS.map((tier, index) => {
              const Icon = tier.icon;
              const price =
                billingCycle === 'monthly' ? tier.monthlyPrice : tier.yearlyPrice;

              return (
                <motion.div
                  key={tier.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 + index * 0.1 }}
                  className={`relative bg-gradient-to-br ${tier.gradient} backdrop-blur-xl rounded-2xl border ${
                    tier.popular
                      ? 'border-amber-500/50 shadow-2xl shadow-amber-500/20 scale-105'
                      : 'border-white/10'
                  } overflow-hidden`}
                >
                  {tier.popular && (
                    <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-amber-500 to-orange-500 py-2 text-center text-sm font-bold">
                      MOST POPULAR
                    </div>
                  )}

                  <div className={`p-8 ${tier.popular ? 'pt-16' : ''}`}>
                    {/* Icon & Name */}
                    <div className="flex items-center space-x-3 mb-4">
                      <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center">
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <h3 className="text-2xl font-bold">{tier.name}</h3>
                    </div>

                    {/* Price */}
                    <div className="mb-4">
                      {tier.monthlyPrice === 0 ? (
                        <div className="text-4xl font-bold">Free</div>
                      ) : (
                        <>
                          <div className="text-5xl font-bold">
                            ${price}
                            <span className="text-xl text-gray-400 font-normal">
                              /mo
                            </span>
                          </div>
                          {billingCycle === 'yearly' && (
                            <div className="text-sm text-gray-400 mt-1">
                              ${(price * 12).toFixed(2)} billed annually
                            </div>
                          )}
                        </>
                      )}
                    </div>

                    {/* Description */}
                    <p className="text-gray-400 text-sm mb-6">{tier.description}</p>

                    {/* Credits Badge */}
                    <div className="mb-6 px-4 py-2 bg-white/5 rounded-lg border border-white/10 text-center">
                      <div className="text-2xl font-bold text-amber-400">
                        {tier.credits}
                      </div>
                      <div className="text-xs text-gray-400">credits/month</div>
                    </div>

                    {/* CTA Button */}
                    <button
                      onClick={() => handleGetStarted(tier.id)}
                      disabled={isLoading === tier.id || !paymentsEnabled || paymentsPaused}
                      className={`w-full py-3 rounded-lg font-semibold transition-all mb-6 disabled:opacity-50 ${
                        tier.popular
                          ? 'bg-gradient-to-r from-amber-500 to-orange-500 hover:shadow-lg hover:shadow-amber-500/50'
                          : 'bg-white/10 hover:bg-white/20 border border-white/20'
                      }`}
                    >
                      {isLoading === tier.id ? (
                        <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                      ) : (
                        tier.ctaText
                      )}
                    </button>

                    {/* Features */}
                    <div className="space-y-3">
                      {tier.features.map((feature, idx) => (
                        <div key={idx} className="flex items-start space-x-2">
                          <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-gray-300">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Credit Costs */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="max-w-3xl mx-auto"
          >
            <div className="bg-gradient-to-br from-purple-500/10 to-indigo-500/10 backdrop-blur-xl rounded-2xl border border-white/10 p-8">
              <h3 className="text-2xl font-bold mb-6 text-center">
                How Credits Work
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {CREDIT_COSTS.map((item) => (
                  <div
                    key={item.tool}
                    className="text-center p-4 bg-white/5 rounded-xl border border-white/10"
                  >
                    <div className="text-lg font-bold text-amber-400 mb-2">
                      {item.tool}
                    </div>
                    <div className="text-sm text-gray-400">{item.cost}</div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* FAQ Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="mt-20 text-center"
          >
            <h3 className="text-2xl font-bold mb-4">Have Questions?</h3>
            <p className="text-gray-400 mb-6">
              Contact us at{' '}
              <a
                href="mailto:support@contentsage.ai"
                className="text-purple-400 hover:text-purple-300 underline"
              >
                support@contentsage.ai
              </a>
            </p>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
